/**
 * Select a Launch Queue.
 */
import {Colors, hexToRGB} from '@wandb/common/css/globals.styles';
import React from 'react';
import {FormatOptionLabelMeta} from 'react-select';
import styled from 'styled-components';

import {Select} from '../Form/Select';
import {Icon, IconMagicWandStick} from '../Icon';
import {Tooltip} from '../Tooltip';
import {ResourceChip} from './ResourceChip';
import * as S from './SelectComponents.styles';
import {isGlobalQueue} from './utils';
import {JsonObject} from './utilTypes';

type CircleProps = {
  isActive: boolean;
};

export const Circle = styled.div<CircleProps>`
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background-color: ${props =>
    props.isActive
      ? hexToRGB(Colors.GREEN, 0.15)
      : hexToRGB(Colors.RED_LIGHT, 0.16)};
  color: ${props => (props.isActive ? Colors.GREEN_DARK : Colors.RED_DARK)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

export type QueueOption = {
  isActive: boolean;
  value: string;
  entity: string;
  project: string;
  queue: string;
  queueID: string;
  defaultResourceConfigID: string;
  resourceType: string;
  config: JsonObject;
};

// This option represents creating a new Starter queue
export const CREATE_QUEUE_PLACEHOLDER: QueueOption = {
  isActive: true,
  value: '',
  entity: '',
  project: '',
  queue: '',
  queueID: '',
  defaultResourceConfigID: '',
  resourceType: 'local-container',
  config: {},
};

export type GroupedQueueOptions = {
  label: string;
  options: QueueOption[];
};

type SelectQueueProps = {
  options: GroupedQueueOptions[];
  defaultQueueOption?: QueueOption;
  onSelectQueue: (option: QueueOption) => void;
};

const OptionLabel = (
  props: QueueOption,
  meta: FormatOptionLabelMeta<QueueOption>
) => {
  const {isActive, entity, queue, resourceType} = props;
  if (queue === '') {
    return (
      <S.SelectOptionLabel>
        <IconMagicWandStick style={{marginRight: 8}} />
        <S.SelectOptionLabel>Create a 'Starter' queue</S.SelectOptionLabel>
        <ResourceChip value="local-container" />
      </S.SelectOptionLabel>
    );
  }

  const isValue = meta.context === 'value'; // selected value, not menu
  const isGlobal = isGlobalQueue(entity, queue);
  const label = isValue && !isGlobal ? `${entity}/${queue}` : queue;
  const icon = isActive ? 'checkmark-circle' : 'warning';
  const tooltip = isActive
    ? 'Queue has at least one running agent'
    : 'You can add items to this queue but it has no running agents';
  return (
    <S.SelectOption>
      <Tooltip
        content={tooltip}
        trigger={
          <Circle isActive={isActive}>
            <Icon name={icon} width={14} height={14} />
          </Circle>
        }
      />
      <S.SelectOptionLabel>{label}</S.SelectOptionLabel>
      <ResourceChip value={resourceType} />
    </S.SelectOption>
  );
};

export const SelectQueue = ({
  options,
  defaultQueueOption,
  onSelectQueue,
}: SelectQueueProps) => {
  const onChange = (option: QueueOption | null) => {
    if (option) {
      onSelectQueue(option);
    }
  };

  return (
    <Select<QueueOption, false, GroupedQueueOptions>
      options={options}
      defaultValue={defaultQueueOption}
      onChange={onChange}
      formatOptionLabel={OptionLabel}
      placeholder="Select a Launch queue"
      noOptionsMessage={({inputValue}) => {
        return !inputValue ? 'No queue available' : 'No results found';
      }}
    />
  );
};
